// src/components/block/liveStreamManagment/WhipEncodingSettingsModal.tsx
// v1.0 — Réglages d'encodage du flux programme (régie → MediaMTX en WHIP).
// v1.1 (2026-10-24) — présentation alignée sur « Config pupitre » (PupitreConfigModal,
//   charte de référence des fenêtres de la régie) : voile flouté, fond de panneau,
//   accent indigo, textes 12–16 px, réglages en cartes Vidéo / Audio. Logique inchangée.
//
// Validation différée, comme AutoDirectorSettingsModal : rien n'est appliqué avant
// « Valider ». Les réglages marqués « au prochain On air » passent par le SDP et
// n'agissent qu'à la prochaine connexion WHIP ; les autres s'appliquent à chaud.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Gauge, Video, AudioLines, RotateCcw, X } from 'lucide-react';

import {
  DEFAULT_WHIP_ENCODING, WHIP_ENCODING_LIMITS,
  WhipEncodingSettings, WhipVideoProfile,
} from '../../../dataTypes/liveStreamManagment/whipEncodingSettings';

interface Props {
  open     : boolean;
  onClose  : () => void;
  settings : WhipEncodingSettings;
  onApply  : (next: WhipEncodingSettings) => void;
  /** Sens du canvas de la chaîne : 1920×1080 en paysage, 1080×1920 en portrait. */
  orientation?: 'landscape' | 'portrait';
}

// Résolutions proposées, du canvas pleine définition vers le plus réduit. `div` est le
// diviseur transmis à l'encodeur (scaleResolutionDownBy) ; le canvas de base fait 1920×1080.
const RESOLUTION_STEPS: { div: number; w: number; h: number; note?: string }[] = [
  { div: 1,   w: 1920, h: 1080, note: ' (pleine définition)' },
  { div: 1.5, w: 1280, h: 720 },
  { div: 2,   w: 960,  h: 540 },
  { div: 3,   w: 640,  h: 360 },
  { div: 4,   w: 480,  h: 270 },
];

// ── Charte (mêmes valeurs que PupitreConfigModal) ─────────────────────────────

const C = {
  text:    '#f1f5f9',
  soft:    '#cbd5e1',
  muted:   '#94a3b8',
  faint:   '#64748b',
  line:    'rgba(255,255,255,0.08)',
  accent:  '#818cf8',
  accentBg:'rgba(99,102,241,0.16)',
  primary: '#6366f1',
};

const LABEL : React.CSSProperties = { fontSize: 13, color: C.text, fontWeight: 600 };
const HINT  : React.CSSProperties = { fontSize: 12, color: C.muted, lineHeight: 1.5 };
const SELECT: React.CSSProperties = { height: 36, padding: '0 10px', borderRadius: 8, background: '#0b1222', border: '1px solid rgba(129,140,248,0.4)', color: C.text, fontSize: 13, colorScheme: 'dark', outline: 'none' };
const NEXT_ON_AIR = (
  <span title="Passe par la négociation SDP : n'agit qu'à la prochaine connexion WHIP"
    style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', color: '#fcd34d', marginLeft: 8, padding: '2px 7px', borderRadius: 999, background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.45)', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
    AU PROCHAIN ON AIR
  </span>
);

const CARD: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 16,
  padding: 16, borderRadius: 12,
  background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.line}`,
};

const BTN_BASE: React.CSSProperties = {
  height: 34, padding: '0 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
};
const BTN_GHOST:   React.CSSProperties = { ...BTN_BASE, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', color: C.soft };
const BTN_PRIMARY: React.CSSProperties = { ...BTN_BASE, background: C.primary, border: '1px solid #818cf8', color: '#fff', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' };

/** Titre d'une carte : pastille icône + libellé (comme les sections de Config pupitre). */
const CardTitle: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <span style={{ width: 28, height: 28, borderRadius: 8, background: C.accentBg, color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>
    <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{label}</span>
    <span style={{ flex: 1, height: 1, background: C.line }} />
  </div>
);

const NumberField: React.FC<{
  label: string; hint: string; unit: string;
  value: number; min: number; max: number; step: number;
  onChange: (v: number) => void;
}> = ({ label, hint, unit, value, min, max, step, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <span style={LABEL}>{label}</span>
      <span style={{
        fontSize: 12, fontWeight: 700, color: '#e0e7ff', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
        padding: '2px 8px', borderRadius: 6, background: C.accentBg, border: '1px solid rgba(129,140,248,0.4)',
      }}>
        {value}{unit}
      </span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(Number(e.target.value))}
      style={{ width: '100%', accentColor: C.primary }}
    />
    <span style={HINT}>{hint}</span>
  </div>
);

function SelectField<T extends string>({ label, hint, value, options, onChange, nextOnAir }: {
  label: string; hint: string; value: T; nextOnAir?: boolean;
  options: { id: T; label: string }[]; onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={LABEL}>{label}{nextOnAir && NEXT_ON_AIR}</span>
      <select className="adm-select" value={value} onChange={e => onChange(e.target.value as T)} style={SELECT}>
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <span style={HINT}>{hint}</span>
    </div>
  );
}

const CheckField: React.FC<{ label: string; hint: string; checked: boolean; onChange: (v: boolean) => void }> =
  ({ label, hint, checked, onChange }) => (
    <label className="adm-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 10px', margin: '-8px -10px', borderRadius: 8 }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ accentColor: C.primary, width: 16, height: 16, marginTop: 1, flexShrink: 0 }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={LABEL}>{label}{NEXT_ON_AIR}</span>
        <span style={HINT}>{hint}</span>
      </span>
    </label>
  );

const WhipEncodingSettingsModal: React.FC<Props> = ({ open, onClose, settings, onApply, orientation = 'landscape' }) => {
  const [local, setLocal] = useState<WhipEncodingSettings>(settings);

  // À chaque ouverture on repart des réglages en vigueur, pas d'un brouillon abandonné.
  useEffect(() => { if (open) setLocal(settings); }, [open, settings]);

  if (!open) return null;

  const L = WHIP_ENCODING_LIMITS;
  const patch = (p: Partial<WhipEncodingSettings>) => setLocal(prev => ({ ...prev, ...p }));
  const apply = () => { onApply(local); onClose(); };

  const portrait = orientation === 'portrait';
  const resolutionOptions = RESOLUTION_STEPS.map(r => ({
    id: String(r.div),
    label: `${portrait ? r.h : r.w}x${portrait ? r.w : r.h}${r.note ?? ''}`,
  }));
  // Réglage mémorisé avant cette liste (diviseur libre) : on le garde visible plutôt que de l'écraser.
  if (!RESOLUTION_STEPS.some(r => r.div === local.scaleResolutionDownBy)) {
    resolutionOptions.push({ id: String(local.scaleResolutionDownBy), label: `Personnalisé (÷${local.scaleResolutionDownBy})` });
  }

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <style>{`
        .adm-select option { background: #1a2035 !important; color: #e2e8f0 !important; }
        .adm-select:focus { border-color: #818cf8 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.25); }
        .adm-row:hover { background: rgba(255,255,255,0.035); }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--color-Panel-niv1-BG)', border: `1px solid ${C.line}`, borderRadius: 16,
          width: 620, maxWidth: '94vw', maxHeight: '88vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.85)',
        }}
      >
        {/* ── En-tête ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
          <span style={{ width: 32, height: 32, borderRadius: 9, background: C.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Gauge style={{ width: 16, height: 16, color: C.accent }} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Encodage de la diffusion</div>
            <div style={{ fontSize: 12, color: C.muted }}>Flux programme envoyé par la régie au serveur (WHIP)</div>
          </div>
          <button onClick={onClose} title="Fermer sans appliquer"
            style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.line}`, color: C.soft, width: 32, height: 32, borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
          <span style={{ ...HINT, padding: '10px 14px', borderRadius: 10, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(129,140,248,0.25)', color: C.soft }}>
            Mémorisé pour cette chaîne sur ce poste. Sauf mention <span style={{ color: '#fcd34d', fontWeight: 700 }}>AU PROCHAIN ON AIR</span>, un changement
            s'applique tout de suite à la diffusion en cours.
          </span>

          {/* ── Vidéo ── */}
          <div style={CARD}>
            <CardTitle icon={<Video style={{ width: 15, height: 15 }} />} label="Vidéo" />

            <SelectField<WhipVideoProfile>
              label="Profil H264" nextOnAir value={local.videoProfile} onChange={v => patch({ videoProfile: v })}
              options={[
                { id: 'auto',     label: 'Automatique (High d\'abord)' },
                { id: 'baseline', label: 'Constrained Baseline' },
                { id: 'main',     label: 'Main' },
                { id: 'high',     label: 'High' },
              ]}
              hint="Main et High compriment mieux (CABAC) mais coûtent plus cher au décodage côté serveur, et ne changent rien si le navigateur encode en logiciel (OpenH264). Un profil absent des capacités du navigateur retombe sur l'automatique. Changer de profil pendant la vie de la chaîne : le ffmpeg aval voit le flux changer."
            />
            <NumberField
              label="Débit maximal" unit=" kb/s" {...L.maxBitrateKbps}
              value={local.maxBitrateKbps} onChange={v => patch({ maxBitrateKbps: v })}
              hint="Plafond seulement : le débit réel est décidé en continu par le contrôle de congestion, selon le lien montant."
            />
            <NumberField
              label="Cadence maximale" unit=" i/s" {...L.maxFramerate}
              value={local.maxFramerate} onChange={v => patch({ maxFramerate: v })}
              hint={`La chaîne aval attend ${DEFAULT_WHIP_ENCODING.maxFramerate} i/s constants : au-dessus, ffmpeg jette des trames ; en dessous, il en duplique.`}
            />
            <SelectField<string>
              label="Résolution envoyée" value={String(local.scaleResolutionDownBy)}
              onChange={v => patch({ scaleResolutionDownBy: Number(v) })}
              options={resolutionOptions}
              hint="Taille du flux envoyé au serveur. Les tailles réduites allègent le lien montant ; le canvas de la régie reste en pleine définition."
            />
            <SelectField<RTCDegradationPreference>
              label="Sous contrainte, sacrifier…" value={local.degradationPreference} onChange={v => patch({ degradationPreference: v })}
              options={[
                { id: 'maintain-resolution', label: 'La cadence (garder la définition)' },
                { id: 'maintain-framerate',  label: 'La définition (garder la cadence)' },
                { id: 'balanced',            label: 'Les deux (équilibré)' },
              ]}
              hint="Garder la cadence évite les trous d'image quand le lien montant sature (la définition baisse à la place). Garder la définition rend la taille des enregistrements proportionnelle au temps (estimation du quota) mais peut faire tomber l'image à 0 i/s."
            />
            <SelectField<WhipEncodingSettings['contentHint']>
              label="Nature du contenu" value={local.contentHint} onChange={v => patch({ contentHint: v })}
              options={[
                { id: 'motion', label: 'Mouvement (fluidité)' },
                { id: 'detail', label: 'Détail (netteté)' },
                { id: 'text',   label: 'Texte' },
                { id: '',       label: 'Aucun (heuristique du navigateur)' },
              ]}
              hint="Oriente les arbitrages de l'encodeur quand le débit manque."
            />
            <SelectField<RTCPriorityType>
              label="Priorité réseau" value={local.networkPriority} onChange={v => patch({ networkPriority: v })}
              options={[
                { id: 'high',     label: 'Haute' },
                { id: 'medium',   label: 'Moyenne' },
                { id: 'low',      label: 'Basse' },
                { id: 'very-low', label: 'Très basse' },
              ]}
              hint="Face aux angles du multi-angle, qui publient sur leurs propres connexions : décide qui le navigateur sacrifie quand le lien sature."
            />
          </div>

          {/* ── Audio ── */}
          <div style={CARD}>
            <CardTitle icon={<AudioLines style={{ width: 15, height: 15 }} />} label="Audio (Opus)" />

            <NumberField
              label="Débit audio" unit=" kb/s" {...L.audioBitrateKbps}
              value={local.audioBitrateKbps} onChange={v => patch({ audioBitrateKbps: v })}
              hint="Plafond appliqué tout de suite ; la cible négociée (maxaveragebitrate) ne change qu'au prochain On air."
            />
            <CheckField
              label="Stéréo" checked={local.audioStereo} onChange={v => patch({ audioStereo: v })}
              hint="Décoché, Opus encode en mono."
            />
            <CheckField
              label="Correction d'erreurs (FEC)" checked={local.audioFec} onChange={v => patch({ audioFec: v })}
              hint="Résiste mieux aux pertes de paquets, au prix d'une partie du débit."
            />
            <CheckField
              label="Coupure des silences (DTX)" checked={local.audioDtx} onChange={v => patch({ audioDtx: v })}
              hint="Économise du débit pendant les silences. Déconseillé en diffusion : le flux aval peut voir des trous."
            />
          </div>
        </div>

        {/* ── Pied ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 20px', borderTop: `1px solid ${C.line}`, flexShrink: 0 }}>
          <button onClick={() => setLocal(DEFAULT_WHIP_ENCODING)} style={BTN_GHOST} title="Remet le brouillon aux valeurs par défaut (rien n'est appliqué avant « Valider »)">
            <RotateCcw style={{ width: 14, height: 14 }} /> Valeurs par défaut
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={BTN_GHOST}>Annuler</button>
            <button onClick={apply} style={BTN_PRIMARY}>Valider</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WhipEncodingSettingsModal;
